import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { db } from '../../firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import styles from './Layout.module.css';

// ============================================================
// SINO — notificações in-app
// Query: Gestor lê por destinatarioPerfil; outros por UID.
// onSnapshot garante atualização em tempo real.
// ============================================================

const TIPO_CFG = {
  PRAZO_VENCIDO:     { label: 'Prazo',  bg: '#FAE8D6', color: '#7A2800' },
  AGUARDANDO_ACEITE: { label: 'Aceite', bg: '#EEEDFE', color: '#534AB7' },
};

function formatarTs(ts) {
  if (!ts) return '';
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    const min = Math.floor((Date.now() - d.getTime()) / 60000);
    if (min < 1) return 'agora';
    if (min < 60) return `há ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `há ${h}h`;
    const dias = Math.floor(h / 24);
    return dias < 7
      ? `há ${dias} d`
      : d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
}

function calcularRota(notif) {
  if (!notif.movimentacaoId) return null;
  if (notif.tipo === 'AGUARDANDO_ACEITE') return '/movimentacao';
  if (notif.tipo === 'PRAZO_VENCIDO')
    return `/movimentacao/historico?destaque=${notif.movimentacaoId}`;
  return null;
}

export default function Sino() {
  const navigate = useNavigate();
  const usuario = useAuthStore((s) => s.usuario);
  const perfil  = useAuthStore((s) => s.perfil);

  const uid = usuario?.uid ?? null;

  const [notificacoes, setNotificacoes] = useState([]);
  const [aberto,   setAberto]   = useState(false);
  const [dropPos,  setDropPos]  = useState({});
  const [marcando, setMarcando] = useState(false);

  const bellRef = useRef(null);
  const dropRef = useRef(null);

  // --- onSnapshot baseado no perfil (sem índice composto) ---
  useEffect(() => {
    if (!uid || !perfil) return;

    const ref = collection(db, 'notificacoes');
    const q =
      (perfil || '').toUpperCase() === 'GESTOR'
        ? query(ref, where('destinatarioPerfil', '==', 'gestor'))
        : query(ref, where('destinatariosUids', 'array-contains', uid));

    const unsub = onSnapshot(
      q,
      (snap) =>
        setNotificacoes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error('[Sino] Erro ao carregar notificações:', err)
    );
    return unsub;
  }, [uid, perfil]);

  // --- Ordenação e divisão client-side ---
  const sorted = useMemo(
    () =>
      [...notificacoes].sort((a, b) => {
        const ta = a.criadoEm?.toDate?.()?.getTime() ?? 0;
        const tb = b.criadoEm?.toDate?.()?.getTime() ?? 0;
        return tb - ta;
      }),
    [notificacoes]
  );

  const naoLidas      = useMemo(() => sorted.filter((n) => !n.lida).slice(0, 30), [sorted]);
  const lidasRecentes = useMemo(() => sorted.filter((n) =>  n.lida).slice(0, 10), [sorted]);
  const totalNaoLidas = useMemo(() => sorted.filter((n) => !n.lida).length,       [sorted]);

  const badge =
    totalNaoLidas === 0 ? null : totalNaoLidas > 99 ? '99+' : String(totalNaoLidas);

  // --- Fechar ao clicar fora ---
  useEffect(() => {
    if (!aberto) return;
    function onDown(e) {
      if (
        dropRef.current && !dropRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setAberto(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [aberto]);

  // --- Abre / fecha — calcula posição no clique ---
  function toggleSino() {
    if (!aberto && bellRef.current) {
      const r = bellRef.current.getBoundingClientRect();
      // dropdown à direita da sidebar, âncora no bottom do botão (expande para cima)
      setDropPos({ left: r.right + 8, bottom: window.innerHeight - r.bottom });
    }
    setAberto((v) => !v);
  }

  // --- Click numa notificação: marca lida + navega ---
  async function handleClickNotif(notif) {
    if (!notif.lida) {
      try {
        await updateDoc(doc(db, 'notificacoes', notif.id), {
          lida: true,
          lidaEm: serverTimestamp(),
        });
      } catch (e) {
        console.error('[Sino] Erro ao marcar lida:', e);
      }
    }
    const rota = calcularRota(notif);
    setAberto(false);
    if (rota) navigate(rota);
  }

  // --- Marcar todas como lidas ---
  async function marcarTodasLidas() {
    const pendentes = sorted.filter((n) => !n.lida);
    if (!pendentes.length || marcando) return;
    setMarcando(true);
    try {
      const batch = writeBatch(db);
      pendentes.forEach((n) =>
        batch.update(doc(db, 'notificacoes', n.id), {
          lida: true,
          lidaEm: serverTimestamp(),
        })
      );
      await batch.commit();
    } catch (e) {
      console.error('[Sino] Erro ao marcar todas como lidas:', e);
    } finally {
      setMarcando(false);
    }
  }

  return (
    <div className={styles.sinoWrap}>
      {/* ---- BOTÃO SINO ---- */}
      <button
        ref={bellRef}
        className={styles.sinoBtn}
        onClick={toggleSino}
        title="Notificações"
        aria-label="Notificações"
      >
        <svg className={styles.navIcon} viewBox="0 0 16 16" fill="none">
          <path
            d="M8 2a4 4 0 00-4 4v2.5L2.5 10h11L12 8.5V6a4 4 0 00-4-4z"
            stroke="currentColor" strokeWidth="1.3"
            strokeLinecap="round" strokeLinejoin="round"
          />
          <path
            d="M6.5 12.5a1.5 1.5 0 003 0"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"
          />
        </svg>
        Notificações
        {badge && <span className={styles.sinoBadge}>{badge}</span>}
      </button>

      {/* ---- DROPDOWN ---- */}
      {aberto && (
        <div
          ref={dropRef}
          className={styles.sinoDropdown}
          style={{ left: dropPos.left, bottom: dropPos.bottom }}
        >
          {/* Cabeçalho */}
          <div className={styles.sinoDropHead}>
            <span className={styles.sinoDropTitle}>
              Notificações
              {badge && <span className={styles.sinoDropBadge}>{badge}</span>}
            </span>
            <button
              className={styles.sinoMarcarTodas}
              onClick={marcarTodasLidas}
              disabled={totalNaoLidas === 0 || marcando}
            >
              {marcando ? 'Marcando...' : 'Marcar todas como lidas'}
            </button>
          </div>

          {/* Lista */}
          <div className={styles.sinoList}>
            {naoLidas.length === 0 && lidasRecentes.length === 0 ? (
              <div className={styles.sinoEmpty}>
                <div style={{ fontSize: 26, marginBottom: 8 }}>🔔</div>
                Nenhuma notificação
              </div>
            ) : (
              <>
                {/* Não lidas */}
                {naoLidas.map((n) => {
                  const tcfg = TIPO_CFG[n.tipo] ?? { label: n.tipo || '?', bg: '#f0f0f0', color: '#555' };
                  return (
                    <div
                      key={n.id}
                      className={`${styles.sinoItem} ${styles.sinoItemNaoLida}`}
                      onClick={() => handleClickNotif(n)}
                    >
                      <div className={styles.sinoItemHead}>
                        <span
                          className={styles.sinoItemTipo}
                          style={{ background: tcfg.bg, color: tcfg.color }}
                        >
                          {tcfg.label}
                        </span>
                        <span className={styles.sinoItemTitulo}>{n.titulo}</span>
                        <span className={styles.sinoItemData}>{formatarTs(n.criadoEm)}</span>
                      </div>
                      {n.descricao && (
                        <div className={styles.sinoItemDesc}>{n.descricao}</div>
                      )}
                    </div>
                  );
                })}

                {/* Divisor + lidas recentes */}
                {lidasRecentes.length > 0 && (
                  <>
                    <div className={styles.sinoLidaDivider}>Lidas</div>
                    {lidasRecentes.map((n) => {
                      const tcfg = TIPO_CFG[n.tipo] ?? { label: n.tipo || '?', bg: '#f0f0f0', color: '#555' };
                      return (
                        <div
                          key={n.id}
                          className={styles.sinoItem}
                          onClick={() => handleClickNotif(n)}
                        >
                          <div className={styles.sinoItemHead}>
                            <span
                              className={styles.sinoItemTipo}
                              style={{ background: tcfg.bg, color: tcfg.color, opacity: 0.6 }}
                            >
                              {tcfg.label}
                            </span>
                            <span
                              className={styles.sinoItemTitulo}
                              style={{ color: '#888', fontWeight: 500 }}
                            >
                              {n.titulo}
                            </span>
                            <span className={styles.sinoItemData}>{formatarTs(n.criadoEm)}</span>
                          </div>
                          {n.descricao && (
                            <div className={styles.sinoItemDesc} style={{ color: '#aaa' }}>
                              {n.descricao}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
